import { useEffect, useState } from "react";
import { api, type FactorMeta, type RankRow } from "../lib/api";

/**
 * The right rail — the reason this is not just another stock screener.
 *
 * Every fundamental shown carries the XBRL tag it came from and the date it became
 * KNOWABLE, so a number can always be traced to its source and checked against the
 * as-of date. A screener shows you a value; this shows you why you were entitled to
 * know it.
 */
export function Evidence({
  row,
  on,
  factor,
}: {
  row: RankRow | null;
  on: string;
  factor: FactorMeta | null;
}) {
  const [concepts, setConcepts] = useState<Array<Record<string, unknown>>>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!row) return;
    let cancelled = false;
    setLoading(true);
    api
      .company(row.cik, on)
      .then((r) => !cancelled && setConcepts(r.concepts))
      .catch(() => !cancelled && setConcepts([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [row, on]);

  if (!row) {
    return <div className="p-3 text-term-dim text-[11px]">Select a name.</div>;
  }

  return (
    <div className="p-3 space-y-4">
      <div>
        <div className="text-base">{row.ticker}</div>
        <div className="text-term-dim text-[11px]">{row.title}</div>
        <div className="text-term-dim text-[10px] mt-1 tnum">CIK {row.cik}</div>
      </div>

      <section>
        <h3 className="text-[10px] uppercase tracking-wide text-term-dim mb-1">Score</h3>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          <span className="text-term-dim">rank</span>
          <span className="tnum text-right">{row.rank}</span>
          <span className="text-term-dim">raw</span>
          <span className="tnum text-right">
            {row.raw_value === null ? "—" : row.raw_value.toFixed(4)}
          </span>
          <span className="text-term-dim">z-score</span>
          <span className="tnum text-right">
            {row.zscore === null ? "—" : row.zscore.toFixed(3)}
          </span>
        </div>
        {factor && (
          <p className="text-term-dim text-[10px] mt-2 leading-relaxed">
            {factor.description}
            {factor.direction === -1 && " Lower is better; the score is inverted accordingly."}
          </p>
        )}
      </section>

      <section>
        <h3 className="text-[10px] uppercase tracking-wide text-term-dim mb-1">
          Fundamentals as known on {on}
        </h3>
        {loading ? (
          <div className="text-term-dim text-[11px] animate-pulse">loading…</div>
        ) : concepts.length === 0 ? (
          <div className="text-term-dim text-[11px]">No filings knowable by this date.</div>
        ) : (
          <table className="w-full">
            <tbody>
              {concepts.map((c, i) => (
                <tr key={i} className="border-b border-term-edge/40 align-top">
                  <td className="py-1 pr-2">
                    <div>{String(c.concept ?? "")}</div>
                    {/* The provenance that makes the number checkable. */}
                    <div className="text-term-dim text-[10px]">{String(c.chosen_tag ?? "")}</div>
                  </td>
                  <td className="py-1 tnum text-right whitespace-nowrap">
                    {typeof c.value === "number" ? compact(c.value) : "—"}
                    <div className="text-term-dim text-[10px]">
                      {String(c.period_end ?? "")}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function compact(n: number): string {
  const abs = Math.abs(n);
  const [div, suffix] =
    abs >= 1e12 ? [1e12, "T"] : abs >= 1e9 ? [1e9, "B"] : abs >= 1e6 ? [1e6, "M"] : [1, ""];
  return `${(n / div).toFixed(suffix ? 2 : 0)}${suffix}`;
}
